import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, Steps, Step, Code, Tag, Screenshot, CodeBlock } from '../../components/DocComponents'

const toc = [
  { id: 'what', title: 'What is a Preparation?', level: 2 as const },
  { id: 'lifecycle', title: 'Lifecycle', level: 2 as const },
  { id: 'create', title: 'Creating a Preparation', level: 2 as const },
  { id: 'step-types', title: 'Step types', level: 2 as const },
  { id: 'payload', title: 'The journey payload', level: 2 as const },
  { id: 'trigger', title: 'Triggering a Preparation', level: 2 as const },
]

export function Preparations() {
  return (
    <Article toc={toc}>
      <H1>Preparations</H1>
      <Lead>A Preparation is a workflow definition — an ordered sequence of Steps. Each time a Preparation runs, a Journey is created to track that specific execution.</Lead>

      <H2 id="what">What is a Preparation?</H2>
      <P>Think of a Preparation like a recipe: it defines the steps to follow. The execution of that recipe on a given occasion is the Journey. You can run the same Preparation many times, each time creating an independent Journey.</P>

      <H2 id="lifecycle">Lifecycle</H2>
      <P>A Journey moves through states as it progresses:</P>
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {[
          { label: 'pending', color: 'slate' as const, desc: 'Created, not yet started' },
          { label: 'running', color: 'blue' as const, desc: 'A step is executing' },
          { label: 'paused', color: 'amber' as const, desc: 'Waiting for user input' },
          { label: 'completed', color: 'green' as const, desc: 'All steps done' },
          { label: 'failed', color: 'red' as const, desc: 'An error occurred' },
        ].map(s => (
          <div key={s.label} className="flex items-center gap-1.5">
            <Tag color={s.color}>{s.label}</Tag>
            <span className="text-xs text-slate-500">{s.desc}</span>
            {s.label !== 'failed' && <span className="text-slate-300">→</span>}
          </div>
        ))}
      </div>
      <Callout type="info">
        A Journey is <Tag color="amber">paused</Tag> when an Interviewer step is reached — the system waits
        for the user to submit their answers. Once submitted, the Journey resumes automatically.
      </Callout>

      <H2 id="create">Creating a Preparation</H2>
      <Steps>
        <Step n={1} title="Open Preparations">Go to <strong>Brainer › Preparations</strong> and click <strong>New Preparation</strong>.</Step>
        <Step n={2} title="Name it">Give it a clear action-oriented name like <Code>Create Invoice</Code> or <Code>Onboard Customer</Code>.</Step>
        <Step n={3} title="Add Steps">In the preparation detail, click <strong>Add Step</strong>. Each step has a type and a position (order).</Step>
        <Step n={4} title="Configure each Step">Open each step to configure it — the options differ by step type (see below).</Step>
      </Steps>
      <Screenshot alt="Preparation detail page showing a list of steps with type badges and an Add Step button" caption="Steps are executed top to bottom. Drag to reorder." />

      <H2 id="step-types">Step types</H2>
      <H3>Interviewer</H3>
      <P>Pauses the journey and presents the user with a form. Each item in the interviewer corresponds to a field to collect. Items can:</P>
      <UL>
        <LI>Create a new record in a Cup (<strong>create</strong> mode)</LI>
        <LI>Update an existing record using a payload value as the ID (<strong>update</strong> mode)</LI>
        <LI>Link to a parent record via a relation (<Code>from_payload</Code> or <Code>belongs_to</Code>)</LI>
      </UL>
      <H3>Generator</H3>
      <P>Runs automatically — no user interaction. Uses a Drinker script to compute values and write them into the journey payload or create/update records.</P>
      <H3>Funnel</H3>
      <P>A conditional branch. Evaluates a condition and routes the journey to a different Preparation based on the result. Used to build branching workflows (if/else logic).</P>

      <H2 id="payload">The journey payload</H2>
      <P>Every Journey carries a <strong>payload</strong> — a key-value map that accumulates data as steps run. Interviewer answers and Generator outputs are stored in the payload. Later steps can read values from earlier steps.</P>
      <CodeBlock language="json" title="Example journey payload after an Interviewer step">
{`{
  "customerId": "aaa-111",
  "productId":  "bbb-222",
  "quantity":   "5",
  "preorderId": "ccc-333"
}`}
      </CodeBlock>
      <Callout type="tip">
        Pass an <strong>initial payload</strong> when triggering a journey to pre-populate values — for example, forwarding a <Code>preorderId</Code> from a Plate button click so the Interviewer can auto-link the new record to the right preorder.
      </Callout>

      <H2 id="trigger">Triggering a Preparation</H2>
      <P>A Preparation can be triggered in several ways:</P>
      <UL>
        <LI><strong>Plate Preparation View</strong> — An embedded interviewer directly inside a Plate. The journey starts automatically when the view is loaded.</LI>
        <LI><strong>Plate Button</strong> — A button navigates to a Plate that contains a Preparation View.</LI>
        <LI><strong>API</strong> — <Code>POST /api/preparations/:id/journeys</Code> with an optional <Code>initialPayload</Code> body.</LI>
        <LI><strong>BUZZ Subscription</strong> — Call the journey API from a webhook triggered by any BUZZ event.</LI>
      </UL>
    </Article>
  )
}
